/**
 * Products index page — grid of all 7 marketing product pages.
 */
import { Link } from "react-router-dom";
import {
  Building2, Brain, LayoutDashboard, LineChart, Users2, Landmark, Target,
  ArrowRight, Sparkles, ArrowUpRight,
} from "lucide-react";
import { MarketingHeader, MarketingFooter } from "../MarketingChrome";

const PRODUCTS = [
  {
    icon: Building2,
    slug: "property-workspace",
    bp: "BP-002",
    title: "Property Workspace",
    tagline: "Every property is a command center.",
    desc: "Plugin-based tabs — Analysis, Comps, Docs, Messages, Coach — surrounding an immutable timeline of every decision.",
  },
  {
    icon: Brain,
    slug: "ai-acquisition-coach",
    bp: "BP-025",
    title: "AI Acquisition Coach",
    tagline: "Evidence-backed next-best-action.",
    desc: "Grounded LLM that synthesises every engine into one recommendation — with a confidence envelope and citations.",
    primary: true,
  },
  {
    icon: LayoutDashboard,
    slug: "executive-dashboard",
    bp: "BP-019 / BP-020",
    title: "Executive Dashboard",
    tagline: "Business health in seconds.",
    desc: "40+ KPIs distilled into 6 dimensions with elite / strong / healthy / watch bands. Drill-through built in.",
  },
  {
    icon: LineChart,
    slug: "market-intelligence",
    bp: "BP-024",
    title: "Market Intelligence",
    tagline: "Every ZIP scored. Every strategy ranked.",
    desc: "0–100 Market Health, strategy fit, 12-month forecast, and opportunity alerts across 42k+ ZIP codes.",
  },
  {
    icon: Users2,
    slug: "buyer-intelligence",
    bp: "BP-022",
    title: "Buyer Intelligence",
    tagline: "Ranked, verified, funded buyers.",
    desc: "Fit-scored per property. Verified proof of funds. Track record captured on the timeline. Top-3 in seconds.",
  },
  {
    icon: Landmark,
    slug: "portfolio-intelligence",
    bp: "BP-023",
    title: "Portfolio Intelligence",
    tagline: "Hold like a fund.",
    desc: "Cash-flow health, cap rate, occupancy, maturity radar, and BRRRR refi opportunities across held assets.",
  },
  {
    icon: Target,
    slug: "strategy-comparison",
    bp: "BP-014",
    title: "Strategy Comparison",
    tagline: "Every strategy, scored side-by-side.",
    desc: "8 exit strategies scored in parallel with a numeric Why-Not audit for every rejected exit.",
  },
];

export default function ProductsIndex() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="products-index">
      <MarketingHeader />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0B1F3A] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(5,150,105,0.22),transparent_60%)]"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-1 rounded-full bg-white/10 border border-white/20 px-3 py-1 text-xs font-semibold">
              <Sparkles className="w-3 h-3 text-emerald-400" />
              Seven engines. One operating system.
            </div>
            <h1 className="mt-6 text-4xl md:text-6xl font-bold tracking-tight leading-[1.05]">
              Products built for<br />
              <span className="text-emerald-400">every stage of the deal.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl">
              Each product is a deterministic engine — versioned, testable, explainable — that plugs into the AI Coach and the Executive Dashboard. Pick what you need. Add the rest when you scale.
            </p>
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PRODUCTS.map(({ icon: Icon, slug, bp, title, tagline, desc, primary }) => (
            <Link
              key={slug}
              to={`/products/${slug}`}
              data-testid={`product-card-${slug}`}
              className={`group relative rounded-2xl border p-6 transition-all hover:shadow-xl ${
                primary
                  ? "border-emerald-500 bg-emerald-50 hover:border-emerald-600"
                  : "border-slate-200 bg-white hover:border-emerald-500"
              }`}
            >
              <div className="flex items-start justify-between">
                <Icon className="w-8 h-8 text-emerald-600" />
                <div className="text-[10px] font-mono text-slate-500">{bp}</div>
              </div>
              <div className="mt-6 font-bold text-xl group-hover:text-emerald-700">
                {title}
              </div>
              <div className="mt-1 text-sm font-semibold text-emerald-700">
                {tagline}
              </div>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{desc}</p>
              <div className="mt-6 text-sm font-semibold text-emerald-600 inline-flex items-center gap-1">
                Explore <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-[#0B1F3A] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(5,150,105,0.22),transparent_70%)]"></div>
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight">
            One platform.<br />
            <span className="text-emerald-400">Every strategy. Every stage.</span>
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/auth?mode=signup"
              data-testid="products-cta-primary"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-3"
            >
              Start Free Trial <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-lg border border-white/20 hover:bg-white/5 text-white font-semibold px-6 py-3"
            >
              Book a live demo
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
